import { API } from "aws-amplify";
import TickerSearchbar from "dashboard/tickers/TickerSearchbar";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import Button from "_shared/components/Button/Button";
import InputField from "_shared/components/InputField/InputField";
import CustomModal from "_shared/components/Modal/Modal";
import { useModal } from "_shared/contexts/ModalContext";
import {
  useAddPanelMutation,
  useAddTickerMutation,
  useUserPanelsQuery,
  useUserSubscriptionsQuery,
  useUserTickersQuery,
} from "_shared/graphql";
import styles from "./NewTabModal.module.scss";
import { analytics } from "_shared/utils/analytics";

interface Props {
  open: boolean;
  onClose: () => void;
}

function NewTabModal({ open, onClose }: Props) {
  const { onErrorMsg } = useModal();

  const [selectedTickers, setSelectedTickers] = useState<string[]>([]);
  const [remainingTickers, setRemainingTickers] = useState<number>();

  const { data: userPanelsData } = useUserPanelsQuery();
  const { data: userTickersData } = useUserTickersQuery();
  const { data: userSubscriptionsData } = useUserSubscriptionsQuery();
  const [addPanel] = useAddPanelMutation();
  const [addTicker] = useAddTickerMutation();

  useEffect(() => {
    if (!userTickersData || !userSubscriptionsData) return;
    const getRemainingTickers = async () => {
      const planDetails = await API.get("public", "/cognito/subscriptions", {});
      const planId =
        userSubscriptionsData?.user_subscriptions[0].subscription_id;

      const tickerLimit = planDetails[planId]?.total_tickers;
      const userTickerCount = userTickersData.ticker.length;

      setRemainingTickers(tickerLimit - userTickerCount);
    };
    getRemainingTickers();
  }, [userTickersData, userSubscriptionsData]);

  const onSubmit = async ({ name }: { name: string }) => {
    // check that panel name is valid
    if (!name.match(/^[a-zA-Z0-9 ]+$/)) {
      onErrorMsg("Panel name can only contain letters, numbers, and spaces");
      return;
    }
    // check that panel name is unique
    if (userPanelsData?.panel.find((panel) => panel.name === name)) {
      onErrorMsg("Panel name must be unique");
      return;
    }

    // create the panel
    const { data } = await addPanel({
      variables: {
        name,
      },
      refetchQueries: ["UserPanels"],
      awaitRefetchQueries: true,
    });
    if (!data?.add_panel?.id) {
      onErrorMsg("Error creating panel");
      return;
    }

    // add tickers to panel
    await Promise.all(
      selectedTickers.map((ticker) =>
        addTicker({
          variables: {
            ticker,
            panelId: data?.add_panel?.id,
          },
        }),
      ),
    );

    analytics.track("Tab Created", {
      panelId: data?.add_panel?.id,
      panelName: name,
      tickers: selectedTickers,
    });

    onClose();
  };

  return (
    <CustomModal open={open} onClose={onClose} title={"Create a new tab"}>
      <Formik initialValues={{ name: "" }} onSubmit={onSubmit}>
        {({ isSubmitting, handleSubmit }) => (
          <form className={styles.form} onSubmit={handleSubmit}>
            <div>
              <div className={styles.inputLabel}>Tab name</div>
              <InputField label="Enter a tab name" name={"name"} dark />
              <div className={styles.inputSubtitle}>
                Tab name can only contain letters, numbers, spaces, and must be
                unique
              </div>
            </div>
            <div>
              <div className={styles.inputLabel}>Tickers</div>
              <div style={{ position: "relative" }}>
                <TickerSearchbar
                  selectedTickerList={selectedTickers}
                  setSelectedTickerList={setSelectedTickers}
                  tickersRemaining={100}
                  setTickersRemaining={() => {}}
                />
                <i
                  className={`${styles.searchIcon} fas fa-search`}
                  style={{ top: 7, left: 10 }}
                />
              </div>
              <div className={styles.inputSubtitle}>
                You have {remainingTickers} tickers remaining in your
                subscription.
              </div>
            </div>
            <div className={styles.footer}>
              <Button onClick={onClose} type="button" variant="secondary">
                Close
              </Button>
              <Button type="submit" loading={isSubmitting}>
                Save
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </CustomModal>
  );
}

export default NewTabModal;
